import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import FirestoreService from '../assets/js/firestore_instance';
import { doc, getDoc, collection, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth, onAuthStateChanged } from 'firebase/auth';


const CreateOrganization = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [mapUrl, setMapUrl] = useState('');
  const [showMap, setShowMap] = useState(false);
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);
  const db = FirestoreService.getFirestore();
  const auth = getAuth();
  const stripeDev = "https://buy.stripe.com/test_3cs3fWel94225jyfYZ"; //dev
  const stripeProd = "https://buy.stripe.com/9AQ7vm9IH0NfaYw5kl"; //prod

  useEffect(() => {
      const authCheck = onAuthStateChanged(auth, async (user) => {
        if (!user) {
          navigate('/sign-in');
          return;
        }
  
        try {
          const docRef = doc(db, "admin_users", user.uid);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
           const org = await getOrganization(docSnap.data().organizationId[0]);
           if (org && org.subscription === 'none') {
              window.location.href = stripeProd;
           }

          }
        } catch (error) {
          console.error("Error:", error);
        }
      });
  
      return () => authCheck();
    }, [auth, db, navigate]);

  // Validation state
  const [errors, setErrors] = useState({
    email: '',
    phone: '',
    name: '',
    website: '',
    logo: ''
  });
  
  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    aboutOrg: '',
    logo: null,
    latitude: '',
    longitude: ''
  });

  const getOrganization = async (orgId) => {
      try {
        const orgRef = doc(db, 'organizations', orgId);
        const orgDoc = await getDoc(orgRef);
        
        if (orgDoc.exists()) {
          return orgDoc.data();
        } else {
          throw new Error("Organization not found");
        }
      } catch (error) {
        console.error("Error fetching organization:", error);
      }
    };

  // Debounce function to prevent too many API calls
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  // Debounced address values
  const debouncedAddress = useDebounce(formData.address, 800);
  const debouncedCity = useDebounce(formData.city, 800);
  const debouncedState = useDebounce(formData.state, 800);
  const debouncedZip = useDebounce(formData.zip, 800);

  // Function to geocode address and display map
  const geocodeAddress = async () => {
    // Only proceed if we have address, city, state, AND zip
    if (!debouncedAddress || !debouncedCity || !debouncedState || !debouncedZip) {
      setShowMap(false);
      return;
    }
  
    try {
      setIsGeocodingLoading(true);
      const fullAddress = `${debouncedAddress}, ${debouncedCity}, ${debouncedState} ${debouncedZip}`.trim();
      const encodedAddress = encodeURIComponent(fullAddress);
      
      // Using Nominatim (OpenStreetMap's geocoding service)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}`,
        { headers: { 'Accept-Language': 'en' } }
      );
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        // Store coordinates in form data
        setFormData(prev => ({
          ...prev,
          latitude: data[0].lat,
          longitude: data[0].lon
        }));
        
        // Generate and set map URL
        // Transport layer: Builds and returns the map URL using the provided coordinates
        const getMapUrl = (lat, lon) => {
            const offset = 0.01;
            return `https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(lon) - offset}%2C${parseFloat(lat) - offset}%2C${parseFloat(lon) + offset}%2C${parseFloat(lat) + offset}&layer=mapnik&marker=${lat}%2C${lon}&layers=T`;
        };

        const url = getMapUrl(data[0].lat, data[0].lon);
        setMapUrl(url);
        setShowMap(true);
      } else {
        setShowMap(false);
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
      setShowMap(false);
    } finally {
      setIsGeocodingLoading(false);
    }
  };

  // Call geocoding when debounced address fields change
  useEffect(() => {
    if (step === 2) { // Only run when on the address step
      geocodeAddress();
    }
  }, [debouncedAddress, debouncedCity, debouncedState, debouncedZip, step]);

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return true; // Email is optional
    return emailRegex.test(email);
  };

  // Phone validation function
  const validatePhone = (phone) => {
    // Allow formats like (123) 456-7890, 123-456-7890, 123.456.7890, 1234567890
    const phoneRegex = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
    if (!phone) return true; // Phone is optional
    return phoneRegex.test(phone);
  };

  // Handle input changes with validation
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (name === 'name') {
      if (!value) {
        setErrors({...errors, name: 'Organization name is required'});
      } else {
        setErrors({...errors, name: ''});
      }
    }

    if (name === 'website') {
      if (!value) {
        setErrors({...errors, website: 'Website is required'});
      } else {
        setErrors({...errors, website: ''});
     }
    }
    
    // Validate email and phone as user types
    if (name === 'email') {
      if (!value) {
        setErrors({...errors, email: 'Email is required'});
      } else if (!validateEmail(value)) {
        setErrors({...errors, email: 'Please enter a valid email address'});
      } else {
        setErrors({...errors, email: ''});
      }
    }
    
    if (name === 'phone') {
      if (!value) {
        setErrors({...errors, phone: 'Phone number is required'});
      } else if (!validatePhone(value)) {
        setErrors({...errors, phone: 'Please enter a valid phone number'});
      } else {
        setErrors({...errors, phone: ''});
      }
    }

    if (name === 'logo') {
        if (!value) {
            setErrors({...errors, logo: 'Organization logo is required'});
        } else {
            setErrors({...errors, logo: ''});
        }
    }

  };

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        logo: file
      });
      
      // Clear any logo error
      setErrors(prev => ({
        ...prev,
        logo: ''
      }));
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Validate form before proceeding to next step
  const validateForm = () => {
    let formIsValid = true;
    const newErrors = { email: '', phone: '', name: '', website: '', logo: '' };
  
    if (!formData.name) {
      newErrors.name = 'Organization name is required';
      formIsValid = false;
    }
  
    if (!formData.website) {
      newErrors.website = 'Website is required';
      formIsValid = false;
    }
        
    if (!formData.email) {
      newErrors.email = 'Email is required';
      formIsValid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      formIsValid = false;
    }
    
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
      formIsValid = false;
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
      formIsValid = false;
    }
    
    // Only check logo validation on final step (or when submitting)
    if (step === 3 && !formData.logo) {
      newErrors.logo = 'Organization logo is required';
      formIsValid = false;
    }
    
    setErrors(newErrors);
    return formIsValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return; // Don't submit if validation fails
    }

    if (!auth.currentUser) {
        console.error('User not authenticated');
        return;
    }
  
    
    setIsLoading(true);

    try {
        //save org to firestore
        const orgRef = collection(db, 'organizations');
        
        const newOrg = {
          organizationName: formData.name,
          subscription: 'none',
          timestamp: serverTimestamp(),
          email: formData.email,
          phone: formData.phone,
          organizationWebsite: formData.website,
          address: {
            street: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            fullAddress: `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip}`.trim(),
            latitude: formData.latitude,
            longitude: formData.longitude
          },
          about: formData.aboutOrg,
          orgType: "joltchat"
        };
        
        // Create the organization document in Firestore
        const docRef = await addDoc(orgRef, newOrg);

        // update admin_users collection with orgId
        const userRef = doc(db, 'admin_users', auth.currentUser.uid);
        await updateDoc(userRef, {
          organizationId: [docRef.id]
        });
        
        // Upload the logo to Firebase Storage
        const storage = FirestoreService.getStorage();
        const orgNameWithoutSpaces = formData.name.replace(/\s+/g, '-').toLowerCase();
        const storageRef = ref(storage, `organization-logos/${orgNameWithoutSpaces}_${docRef.id}`);
        
        // Upload file and get snapshot
        await uploadBytes(storageRef, formData.logo)
          .then(async (snapshot) => {
            console.log('Uploaded logo successfully!');
            
            // Get the download URL
            const downloadURL = await getDownloadURL(snapshot.ref);
            console.log('File available at', downloadURL);
            
            // Update organization document with ID and logo URL
            await updateDoc(docRef, {
              organizationId: docRef.id,
              organizationAvatar: downloadURL,
            });
            
            // Update form data with URL (optional)
            setFormData(prev => ({
              ...prev,
              logo: downloadURL
            }));

            // navigate to subscription page
            window.location.href = stripeProd;
            
          });
          
      } catch (error) {
        console.error('Error creating organization:', error);
      } finally {
        setIsLoading(false);
      }


  };

  // Next step handler with validation
  const handleNextStep = () => {
    // For step 1, we need both valid email and phone
    if (step === 1) {
      const isValid = validateForm();
      if (isValid) {
        setStep(step + 1);
      }
    } else {
      // For other steps, just proceed
      setStep(step + 1);
    }
  };

  // Previous step handler
  const handlePrevStep = () => {
    setStep(step - 1);
  };

  return (
    <div className="dashboard-element-bg min-h-screen min-w-screen flex items-center justify-center py-12 pt-28">
      <motion.div 
        className="max-w-4xl w-full mx-auto dashboard-main-color p-8 rounded-3xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="text-center mb-10">
          <motion.h1 
            className="text-3xl font-bold mb-2 text-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Create Organization
          </motion.h1>
          <motion.p 
            className="text-gray-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Set up your organization to get started with JoltChat
          </motion.p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-10">
          <div className="flex items-center">
            <motion.div 
              className={`rounded-full h-10 w-10 flex items-center justify-center ${step >= 1 ? 'primary-gradient' : 'dashboard-on-element'}`}
              whileHover={{ scale: 1.05 }}
            >
              <span className="text-white">1</span>
            </motion.div>
            <div className={`h-1 w-16 ${step >= 2 ? 'primary-gradient' : 'dashboard-on-element'}`}></div>
            <motion.div 
              className={`rounded-full h-10 w-10 flex items-center justify-center ${step >= 2 ? 'primary-gradient' : 'dashboard-on-element'}`}
              whileHover={{ scale: 1.05 }}
            >
              <span className="text-white">2</span>
            </motion.div>
            <div className={`h-1 w-16 ${step >= 3 ? 'primary-gradient' : 'dashboard-on-element'}`}></div>
            <motion.div 
              className={`rounded-full h-10 w-10 flex items-center justify-center ${step >= 3 ? 'primary-gradient' : 'dashboard-on-element'}`}
              whileHover={{ scale: 1.05 }}
            >
              <span className="text-white">3</span>
            </motion.div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <div className="dashboard-element-bg p-8 rounded-2xl">
                <h2 className="text-xl font-semibold mb-6 text-gray-200">Organization Details</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-300 mb-2" htmlFor="name">Organization Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`dashboard-on-element w-full p-3 rounded-lg text-white focus:ring-2 focus:ring-red-400 focus:outline-none ${errors.name ? 'ring-2 ring-red-500' : 'focus:ring-red-400'}`}
                      placeholder="e.g., The Grove"
                      required
                    />
                    {errors.name && (
                        <p className="mt-1 text-red-500 text-sm">{errors.name}</p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-300 mb-2" htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`dashboard-on-element w-full p-3 rounded-lg text-white focus:ring-2 focus:outline-none ${errors.email ? 'ring-2 ring-red-500' : 'focus:ring-red-400'}`}
                      placeholder="contact@example.org"
                      required
                    />
                    {errors.email && (
                      <p className="mt-1 text-red-500 text-sm">{errors.email}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2" htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`dashboard-on-element w-full p-3 rounded-lg text-white focus:ring-2 focus:outline-none ${errors.phone ? 'ring-2 ring-red-500' : 'focus:ring-red-400'}`}
                      placeholder="(555) 555-5555"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-red-500 text-sm">{errors.phone}</p>
                    )}
                  </div>
                </div>
                  
                  <div>
                    <label className="block text-gray-300 mb-2" htmlFor="website">Website</label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className={`dashboard-on-element w-full p-3 rounded-lg text-white focus:ring-2 focus:ring-red-400 focus:outline-none ${errors.website ? 'ring-2 ring-red-500' : 'focus:ring-red-400'}`}
                      placeholder="https://www.example.org"
                      required
                    />
                    {errors.website && (
                        <p className="mt-1 text-red-500 text-sm">{errors.website}</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end">
                <motion.button
                  type="button"
                  onClick={handleNextStep}
                  className="primary-gradient text-white px-8 py-3 rounded-full font-medium"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Next Step
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Address */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <div className="dashboard-element-bg p-8 rounded-2xl">
                <h2 className="text-xl font-semibold mb-6 text-gray-200">Location Information</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-300 mb-2" htmlFor="address">Street Address</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className="dashboard-on-element w-full p-3 rounded-lg text-white focus:ring-2 focus:ring-red-400 focus:outline-none"
                      placeholder="123 Church Street"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-gray-300 mb-2" htmlFor="city">City</label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="dashboard-on-element w-full p-3 rounded-lg text-white focus:ring-2 focus:ring-red-400 focus:outline-none"
                        placeholder="San Diego"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2" htmlFor="state">State</label>
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className="dashboard-on-element w-full p-3 rounded-lg text-white focus:ring-2 focus:ring-red-400 focus:outline-none"
                        placeholder="CA"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2" htmlFor="zip">ZIP Code</label>
                      <input
                        type="text"
                        id="zip"
                        name="zip"
                        value={formData.zip}
                        onChange={handleChange}
                        className="dashboard-on-element w-full p-3 rounded-lg text-white focus:ring-2 focus:ring-red-400 focus:outline-none"
                        placeholder="92101"
                      />
                    </div>
                  </div>
                  
                  {/* Map Display */}
                  <div className="mt-4">
                    {isGeocodingLoading ? (
                        <div className="flex items-center justify-center h-20">
                        <div className="text-gray-400">Locating address...</div>
                        </div>
                    ) : showMap ? (
                        <div>
                        <div className="flex items-center mb-2">
                            <svg className="h-5 w-5 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-green-500 text-sm">Address located!</span>
                        </div>
                        <div className="rounded-lg overflow-hidden border-2 border-gray-700">
                            <iframe
                            title="Location Map"
                            width="100%"
                            height="300"
                            style={{ border: 0 }}
                            src={mapUrl}
                            allowFullScreen
                            ></iframe>
                        </div>
                        </div>
                    ) : formData.address && formData.city && formData.state && formData.zip ? (
                        <div className="text-yellow-500 text-sm flex items-center">
                        <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Couldn't locate this address. Please check the details.
                        </div>
                    ) : null}
                    </div>
                  <div>
                    <label className="block text-gray-300 mb-2" htmlFor="aboutOrg">About Your Organization</label>
                    <textarea
                      id="aboutOrg"
                      name="aboutOrg"
                      value={formData.aboutOrg}
                      onChange={handleChange}
                      rows="4"
                      className="dashboard-on-element w-full p-3 rounded-lg text-white focus:ring-2 focus:ring-red-400 focus:outline-none"
                      placeholder="Tell us a bit about your organization..."
                    ></textarea>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between">
                <motion.button
                  type="button"
                  onClick={handlePrevStep}
                  className="bg-gray-700 text-white px-8 py-3 rounded-full font-medium"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Previous
                </motion.button>
                <motion.button
                  type="button"
                  onClick={handleNextStep}
                  className="primary-gradient text-white px-8 py-3 rounded-full font-medium"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Next Step
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Logo Upload */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <div className="dashboard-element-bg p-8 rounded-2xl">
                <h2 className="text-xl font-semibold mb-6 text-gray-200">Organization Logo</h2>
                
                <div className="flex flex-col items-center space-y-4">
  <div className={`w-48 h-48 rounded-full overflow-hidden border-4 ${errors.logo ? 'border-red-500' : 'border-gray-700'} dashboard-on-element flex items-center justify-center`}>
    {previewImage ? (
      <img 
        src={previewImage} 
        alt="Logo Preview" 
        className="w-full h-full object-cover"
      />
    ) : (
      <div className="text-gray-400 text-center p-6">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p>Upload your logo</p>
      </div>
    )}
  </div>
  
  <label className="primary-gradient text-white px-6 py-3 rounded-full font-medium cursor-pointer inline-block">
    <span>{previewImage ? 'Change Logo' : 'Upload Logo'}</span>
    <input
      type="file"
      id="logo"
      name="logo"
      onChange={handleFileChange}
      accept="image/*"
      className="hidden"
    />
  </label>
  
  {errors.logo && (
    <p className="text-red-500 text-sm">{errors.logo}</p>
  )}
  
  <p className="text-gray-400 text-sm text-center max-w-md">
    Upload a high-quality image of your organization's logo. Recommended size: 512x512 pixels or larger.
  </p>
</div>
              </div>
              
              <div className="dashboard-element-bg p-8 rounded-2xl">
                <h2 className="text-xl font-semibold mb-6 text-gray-200">Review & Submit</h2>
                <p className="text-gray-400 mb-4">
                  Please review your information before submitting. You can make changes at any time from your dashboard settings.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300">
                  <div>
                    <p className="text-gray-400">Organization Name</p>
                    <p className="font-medium">{formData.name || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Email</p>
                    <p className="font-medium">{formData.email || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Phone</p>
                    <p className="font-medium">{formData.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Website</p>
                    <p className="font-medium">{formData.website || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Address</p>
                    <p className="font-medium">
                      {formData.address && `${formData.address}, `}
                      {formData.city && `${formData.city}, `}
                      {formData.state && `${formData.state} `}
                      {formData.zip && formData.zip}
                      {!formData.address && !formData.city && !formData.state && !formData.zip && 'Not provided'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between">
                <motion.button
                  type="button"
                  onClick={handlePrevStep}
                  className="bg-gray-700 text-white px-8 py-3 rounded-full font-medium"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Previous
                </motion.button>
                <motion.button
  type="submit"
  className="primary-gradient text-white px-8 py-3 rounded-full font-medium flex items-center"
  whileHover={{ scale: 1.05 }}
  whileTap={{ scale: 0.95 }}
  disabled={isLoading}
  onClick={(e) => {
    if (!formData.logo) {
      e.preventDefault();
      setErrors(prev => ({
        ...prev,
        logo: 'Organization logo is required'
      }));
      return;
    }
  }}
>
  {isLoading ? (
    <>
      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Creating...
    </>
  ) : (
    'Create Organization'
  )}
</motion.button>
              </div>
            </motion.div>
          )}
        </form>
      </motion.div>
    </div>
  );
};

export default CreateOrganization;